import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "phoneNumber",
    "email",
    "address",
    "address2",
    "city",
    "state",
    "zip",
    "projectType",
    "hiddenCustomerName",
    "contactsLoading",
    "contactsFailed",
  ];

  customerSelected(event) {
    this.contactsRequest?.abort();
    this.showContactsStatus(this.contactsFailedTargets, false);

    if (event.detail.value === "") {
      this.element.reset();
      this.clearAutofilledField(this.phoneNumberTarget, "phone-numbers");
      this.clearAutofilledField(this.emailTarget, "emails");
      return;
    }

    try {
      const address = JSON.parse(event.detail.value);

      if (typeof address === "object" && address !== null) {
        this.populateAddress(address);
        this.clearAutofilledField(this.phoneNumberTarget, "phone-numbers");
        this.clearAutofilledField(this.emailTarget, "emails");
        this.loadContacts(address.contacts_url);
      } else {
        this.hiddenCustomerNameTarget.value = address;
      }
    } catch (err) {
      // they entered a new customer name, so the detail wasn't JSON
      this.hiddenCustomerNameTarget.value = event.detail.value;
    }
  }

  async loadContacts(url) {
    const request = new AbortController();
    this.contactsRequest = request;
    this.showContactsStatus(this.contactsLoadingTargets, true);
    let failed = false;

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: request.signal,
      });

      if (response.ok) {
        const contacts = await response.json();
        this.fillBlankField(this.phoneNumberTarget, contacts.phone_numbers, "phone-numbers");
        this.fillBlankField(this.emailTarget, contacts.emails, "emails");
        failed = contacts.refresh_failed;
      } else {
        failed = true;
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      failed = true;
    } finally {
      if (this.contactsRequest === request) {
        this.showContactsStatus(this.contactsLoadingTargets, false);
        this.showContactsStatus(this.contactsFailedTargets, failed);
      }
    }
  }

  showContactsStatus(statuses, shown) {
    statuses.forEach((status) => {
      status.classList.toggle("hidden", !shown);
      status.classList.toggle("inline-flex", shown);
    });
  }

  // Contacts arrive after the contractor may have started typing, so they only
  // fill a field that's still blank, and only replace what we filled ourselves.
  fillBlankField(target, items, datalistPrefix) {
    this.clearDatalist(target, datalistPrefix);

    if (!items || items.length === 0) return;

    if (items.length === 1 && target.value === "") {
      target.value = this.getItemValue(items[0]);
      target.dataset.autofilledValue = target.value;
    } else {
      this.createDatalist(target, items, datalistPrefix);
    }
  }

  clearAutofilledField(target, datalistPrefix) {
    this.clearDatalist(target, datalistPrefix);

    if (target.value === target.dataset.autofilledValue) {
      target.value = "";
    }

    delete target.dataset.autofilledValue;
  }

  populateAddress(address) {
    this.hiddenCustomerNameTarget.value = address.customer_name;

    if (address.street) {
      this.addressTarget.value = address.street;
      this.addressTarget.dispatchEvent(new Event("input"));
    }

    if (address.unit) {
      this.address2Target.value = address.unit;
    }

    if (address.state) {
      this.stateTarget.value = address.state;
    }

    if (address.city) {
      this.cityTarget.value = address.city;
    }

    if (address.zip) {
      this.zipTarget.value = address.zip;
    }

    const projectTypeRadio = this.projectTypeTargets.find(
      (radio) => radio.value === address.customer_type
    );

    if (projectTypeRadio) {
      projectTypeRadio.checked = true;
    }
  }

  createDatalist(target, items, datalistPrefix) {
    const datalistId = `${datalistPrefix}-datalist`;
    const datalist = document.createElement("datalist");
    datalist.id = datalistId;

    items.forEach((item) => {
      const option = document.createElement("option");
      option.value = this.getItemValue(item);
      option.label = this.getItemLabel(item);
      datalist.appendChild(option);
    });

    target.setAttribute("list", datalistId);
    target.parentNode.appendChild(datalist);
  }

  getItemValue(item) {
    return typeof item === "object" ? item.value : item;
  }

  getItemLabel(item) {
    if (typeof item === "object") {
      return item.label;
    }

    return item;
  }

  clearDatalist(target, datalistPrefix) {
    const datalistId = `${datalistPrefix}-datalist`;
    const existingDatalist = document.getElementById(datalistId);
    if (existingDatalist) {
      existingDatalist.remove();
    }

    if (target) {
      target.removeAttribute("list");
    }
  }
}
